import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, HelpCircle, Loader2, X, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSystemStatus, statusSegments } from "@/lib/systemStatus";
import { useT52 } from "@/i18n/v52";

const SEG_ICON = { ok: CheckCircle2, fail: AlertTriangle, unk: HelpCircle, pending: Loader2 };
const SVC_ICON = { ok: CheckCircle2, fail: AlertTriangle, unknown: HelpCircle };
const CHIP_CLS = { ok: "ok", fail: "fail", unknown: "unk" };

function segText(t52, seg) {
  if (seg.n == null) return seg.k === "pending" ? t52("stPending") : seg.k === "unk" ? t52("stUnknownAll") : t52("stNotAvailable");
  return t52({ ok: "stOk", fail: "stFail", unk: "stUnk" }[seg.k]);
}

export function StatusSegments() {
  const st = useSystemStatus();
  const { t52 } = useT52();
  return statusSegments(st).map((seg) => {
    const Icon = SEG_ICON[seg.k];
    return (
      <span key={seg.k} className={`f52-seg ${seg.k}`} data-testid={`status-seg-${seg.k}`}>
        <Icon aria-hidden="true" className={seg.k === "pending" ? "f52-spin" : ""} />
        {seg.n != null && seg.n}<span className="seg-text">{seg.n != null ? "\u00a0" : ""}{segText(t52, seg)}</span>
      </span>
    );
  });
}

export function useStatusSummaryText() {
  const st = useSystemStatus();
  const { t52 } = useT52();
  return statusSegments(st).map((s) => `${s.n != null ? `${s.n} ` : ""}${segText(t52, s)}`).join(", ");
}

export function StatusBadge({ badgeRef }) {
  const st = useSystemStatus();
  const { t52 } = useT52();
  const summary = useStatusSummaryText();
  const hasFail = statusSegments(st).some((s) => s.k === "fail");
  const toggle = () => { if (!st.panelOpen) st.check(); st.setPanelOpen(!st.panelOpen); };
  return (
    <button ref={badgeRef} type="button" className={`f52-status${hasFail ? " has-fail" : ""}`} onClick={toggle}
      aria-expanded={st.panelOpen} aria-controls="f52-status-panel" aria-label={t52("stAria", { summary })} data-testid="system-status-badge">
      <span className="f52-st-label">{t52("status")}</span><StatusSegments />
    </button>
  );
}

export function StatusPanel({ badgeRef }) {
  const st = useSystemStatus();
  const { user } = useAuth();
  const { t52, locale } = useT52();
  const ref = useRef(null);
  const { panelOpen, setPanelOpen } = st;

  useEffect(() => {
    if (!panelOpen) return undefined;
    ref.current?.querySelector("[data-st-close]")?.focus();
    const onKey = (e) => { if (e.key === "Escape") setPanelOpen(false); };
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || badgeRef.current?.contains(e.target)) return;
      setPanelOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    const badge = badgeRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      if (badge && badge.offsetParent !== null) badge.focus();
    };
  }, [panelOpen, setPanelOpen, badgeRef]);

  if (!panelOpen) return null;
  const services = st.data?.services || [];
  const time = st.data?.checked_at ? new Date(st.data.checked_at).toLocaleString(locale) : null;
  return (
    <div id="f52-status-panel" ref={ref} className="f52-st-panel" role="dialog" aria-labelledby="f52-st-title" data-testid="system-status-panel">
      <div className="f52-st-head">
        <h2 id="f52-st-title">{t52("status")}</h2>
        <button type="button" className="f52-icon-btn" aria-label={t52("stClose")} data-st-close onClick={() => setPanelOpen(false)} data-testid="system-status-close"><X aria-hidden="true" /></button>
      </div>
      <p className="f52-st-meta" data-testid="status-checked-at">{st.phase === "loading" ? t52("stChecking") : time ? t52("stChecked", { time }) : "—"}</p>
      {st.phase === "forbidden" && <p className="f52-st-msg" data-testid="status-forbidden">{t52("stForbidden")}</p>}
      {st.phase === "error" && <p className="f52-st-msg" data-testid="status-error">{t52("stError")}</p>}
      {st.phase === "ready" && (
        <ul className="f52-st-list">
          {services.map((s) => {
            const Icon = SVC_ICON[s.status] || HelpCircle;
            return (
              <li key={s.key} data-testid={`status-service-${s.key}`}>
                <span className={`f52-st-ico ${CHIP_CLS[s.status] || "unk"}`}><Icon aria-hidden="true" /></span>
                <div><div className="nm">{t52(`svc_${s.key}`)}</div><div className="md">{t52(`rs_${s.reason}`)}</div></div>
                <span className={`f52-chip ${CHIP_CLS[s.status] || "unk"}`}>{t52(`chip_${s.status}`)}</span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="f52-st-note">{t52("stNote")}</p>
      <div className="f52-st-actions">
        <button type="button" className="f52-btn f52-btn-secondary" onClick={st.check} disabled={st.phase === "loading"} data-testid="system-status-recheck"><RefreshCw aria-hidden="true" />{t52("stRecheck")}</button>
        {user?.permissions?.system?.read && (
          <Link className="f52-btn f52-btn-primary" to="/system" onClick={() => setPanelOpen(false)} data-testid="system-status-open-page">{t52("stOpenPage")}</Link>
        )}
      </div>
    </div>
  );
}
